/*
 * SuperCalc Pro - Monte Carlo Simulation Engine
 * Single source of truth: delegates to AdvancedCalculatorShared strategy series
 * (same TBC split, accumulation tax, phased retirement, and Age Pension logic as historical runs).
 */

import { SimulationEngine } from './SimulationEngine';
import { historicalData } from './data';
import {
    computeFixedSustainableBinarySearch,
    computeFloorCeilingSeries,
    computeVanguardGuardrailsSeries,
    computeDynamicSWRSeries,
} from './AdvancedCalculatorShared';

const DEFAULT_TRIALS = 1000;
const SURVIVAL_TOLERANCE = 100;

/** Build random historical return path with calendar years for FY-aware tax. Fees applied in shared engine — not here. */
export function buildMonteCarloReturnPath(yearsCount, portfolio) {
    const allYears = Object.keys(historicalData).map((y) => Number(y));
    const path = [];
    for (let i = 0; i < yearsCount; i++) {
        const year = allYears[Math.floor(Math.random() * allYears.length)];
        const yearData = historicalData[year];
        const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
        const histInfl = historicalData[year]?.inflation ?? 0;
        const r = ((1 + nominalR) / (1 + histInfl)) - 1;
        path.push({ year, r });
    }
    return path;
}

function getStrategySeriesFn(strategy) {
    switch (String(strategy)) {
        case 'FloorAndCeiling':
            return computeFloorCeilingSeries;
        case 'VanguardGuardrails':
            return computeVanguardGuardrailsSeries;
        case 'DynamicSWR':
            return computeDynamicSWRSeries;
        default:
            return computeFixedSustainableBinarySearch;
    }
}

function medianOf(values) {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
        ? sorted[mid]
        : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

function percentile(sortedAsc, p) {
    if (!sortedAsc.length) return 0;
    const idx = Math.max(0, Math.min(sortedAsc.length - 1, Math.round((p / 100) * (sortedAsc.length - 1))));
    return sortedAsc[idx];
}

/**
 * Run Monte Carlo simulation using shared historical strategy engines.
 * @param {object} inputs - Advanced calculator inputs (same shape as historical runs)
 * @param {object} [options]
 * @param {number} [options.trials=1000]
 * @returns {object} successRate, balancePercentiles, incomePercentiles, medianIncomesSummary
 */
export const runMonteCarloSimulation = (inputs, options = {}) => {
    const state = createMonteCarloState(inputs, options);
    if (!state) return emptyMonteCarloResult();
    for (let t = 0; t < state.trials; t++) {
        runMonteCarloTrial(state);
    }
    return finalizeMonteCarloState(state);
};

/**
 * Async Monte Carlo: runs trials in small chunks and yields to the browser between
 * chunks so results UI can paint/scroll while MC is still computing.
 * @param {object} inputs
 * @param {object} [options]
 * @param {number} [options.trials=1000]
 * @param {number} [options.chunkSize] - trials per chunk (default scales with period length)
 * @param {() => boolean} [options.shouldCancel] - return true to abort
 * @returns {Promise<object>}
 */
export const runMonteCarloSimulationAsync = (inputs, options = {}) => {
    const state = createMonteCarloState(inputs, options);
    if (!state) return Promise.resolve(emptyMonteCarloResult());

    const yearsCount = Math.max(1, Number(inputs.periodLength) || 1);
    // Longer periods are heavier per trial — keep chunks small so paints stay responsive.
    const chunkSize = Math.max(
        5,
        Math.min(40, options.chunkSize ?? Math.floor(400 / yearsCount))
    );

    return new Promise((resolve) => {
        let t = 0;
        const step = () => {
            if (typeof options.shouldCancel === 'function' && options.shouldCancel()) {
                resolve(emptyMonteCarloResult());
                return;
            }
            const end = Math.min(t + chunkSize, state.trials);
            for (; t < end; t++) {
                runMonteCarloTrial(state);
            }
            if (t < state.trials) {
                setTimeout(step, 0);
                return;
            }
            resolve(finalizeMonteCarloState(state));
        };
        setTimeout(step, 0);
    });
};

function emptyMonteCarloResult() {
    return {
        successRate: 0,
        balancePercentiles: { p0: 0, p10: 0, p25: 0, p50: 0, p75: 0, p90: 0 },
        incomePercentiles: { p0: 0, p10: 0, p25: 0, p50: 0, p75: 0, p90: 0 },
        medianIncomesSummary: null,
    };
}

function createMonteCarloState(inputs, options = {}) {
    try {
        const {
            periodLength,
            sp500, ausShares, intlShares, bonds, cash, frankingCredits,
            monteCarloTargetIncome,
            withdrawalStrategy,
        } = inputs;

        const yearsCount = Math.max(0, Number(periodLength) || 0);
        const targetIncomeMC = Number(monteCarloTargetIncome) || 0;
        const strategyNow = String(withdrawalStrategy || 'fixed');
        const trials = options.trials ?? DEFAULT_TRIALS;

        const portfolio = {
            sp500: Number(sp500) || 0,
            ausShares: Number(ausShares) || 0,
            intlShares: Number(intlShares) || 0,
            bonds: Number(bonds) || 0,
            cash: Number(cash) || 0,
            frankingCredits: Number(frankingCredits) || 0,
            infrastructurePct: Number(inputs.infrastructurePct) || 0,
            infrastructureReturnRate: Number(inputs.infrastructureReturnRate) || 0,
        };

        return {
            inputs,
            yearsCount,
            targetIncomeMC,
            trials,
            portfolio,
            seriesFn: getStrategySeriesFn(strategyNow),
            finals: [],
            incomeMaxes: [],
            trialMedianIncomes: [],
            successes: 0,
        };
    } catch (error) {
        console.error('Monte Carlo calculation error:', error);
        return null;
    }
}

function runMonteCarloTrial(state) {
    const { inputs, yearsCount, targetIncomeMC, portfolio, seriesFn } = state;
    const providedReturns = buildMonteCarloReturnPath(yearsCount, portfolio);
    const pathInputs = {
        ...inputs,
        providedReturns,
        startYear: providedReturns[0]?.year ?? inputs.startYear ?? 1980,
        monteCarloTargetIncome: targetIncomeMC,
    };

    // Max sustainable income on this path (full engine — TBC, accum tax, pension, phased)
    const maxResult = computeFixedSustainableBinarySearch(pathInputs, true, true);
    const maxIncome = Number(maxResult.avgIncome) || 0;
    state.incomeMaxes.push(Math.round(maxIncome));

    if (maxIncome + SURVIVAL_TOLERANCE >= targetIncomeMC) {
        state.successes += 1;
    }

    const lastRow = maxResult.incomeByYear?.[maxResult.incomeByYear.length - 1];
    const finalBal = lastRow?.balanceWithTargetIncome ?? lastRow?.balance ?? maxResult.finalBalance ?? 0;
    state.finals.push(Math.round(finalBal));

    // Strategy-specific median income for this path
    const seriesResult = seriesFn({ ...pathInputs, monteCarloTargetIncome: undefined });
    const incomes = (seriesResult.incomeByYear || [])
        .map((row) => Number(row.totalIncome) || 0)
        .filter((n) => n > 0);
    if (incomes.length) {
        state.trialMedianIncomes.push(medianOf(incomes));
    }
}

function finalizeMonteCarloState(state) {
    const { finals, incomeMaxes, trialMedianIncomes, successes, trials } = state;
    finals.sort((a, b) => a - b);
    incomeMaxes.sort((a, b) => a - b);
    trialMedianIncomes.sort((a, b) => a - b);

    const balancePercentiles = {
        p0: finals[0] ?? 0,
        p10: percentile(finals, 10),
        p25: percentile(finals, 25),
        p50: percentile(finals, 50),
        p75: percentile(finals, 75),
        p90: percentile(finals, 90),
    };

    const incomePercentiles = {
        p0: incomeMaxes[0] ?? 0,
        p10: percentile(incomeMaxes, 10),
        p25: percentile(incomeMaxes, 25),
        p50: percentile(incomeMaxes, 50),
        p75: percentile(incomeMaxes, 75),
        p90: percentile(incomeMaxes, 90),
    };

    let medianIncomesSummary = null;
    if (trialMedianIncomes.length) {
        medianIncomesSummary = {
            worst: trialMedianIncomes[0],
            median: medianOf(trialMedianIncomes),
            best: trialMedianIncomes[trialMedianIncomes.length - 1],
        };
    }

    return {
        successRate: Math.round((successes / trials) * 100),
        balancePercentiles,
        incomePercentiles,
        medianIncomesSummary,
    };
}
